import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useDesign } from '../contexts/DesignContext';

interface JobSavingsOpportunity {
  jobName: string;
  jobId: string;
  currentCost: number;
  potentialSavings: number;
}

interface SavingsOpportunitiesProps {
  opportunities: JobSavingsOpportunity[];
  fontSize: number;
}

const SavingsOpportunities: React.FC<SavingsOpportunitiesProps> = ({ opportunities }) => {
  const { theme } = useTheme();
  const { design } = useDesign();
  const isDark = theme === 'dark';
  
  // Sort opportunities by potential savings (descending)
  const sortedOpportunities = [...opportunities].sort((a, b) => b.potentialSavings - a.potentialSavings);
  
  // Format currency values
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };
  
  // Colors from design config
  const bgColor = isDark ? design.colors.darkBackground : design.colors.lightBackgroundSecondary;
  const textColor = isDark ? design.colors.textDark.secondary : design.colors.textLight.secondary;
  const borderColor = isDark ? design.colors.darkBackgroundSecondary : '#E5E7EB';
  const highlightColor = isDark ? design.colors.flarionYellow : design.colors.offBlack;
  const barBgColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
  const barColor = isDark ? design.colors.flarionYellow : design.colors.offBlack; // Single color for the bar
  
  return (
    <div 
      className={`rounded-lg shadow overflow-hidden h-full p-${design.layout.padding.card} ${isDark ? 'dark-theme' : 'light-theme'}`}
      style={{ 
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div className={`mb-${design.layout.margins.title}`}>
        <h2 
          className="text-2xl" 
          style={{ 
            color: textColor,
            fontFamily: design.typography.fontFamily,
            fontSize: `${design.typography.fontSizes['2xl']}px`,
          }}
        >
          Top Savings Opportunities
        </h2>
      </div>
      
      <div 
        className="space-y-6 custom-scrollbar" 
        style={{ 
          flex: '1 1 auto',
          overflowY: 'scroll',
          scrollbarWidth: 'thin',
          scrollbarColor: isDark ? 'rgba(255, 255, 255, 0.3) rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.3) rgba(255, 255, 255, 0.2)',
          paddingRight: '8px',
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'scrollbar', /* IE and Edge */
        }}
      >
        {sortedOpportunities.map((job) => (
          <div key={job.jobId}>
            <div 
              className="px-4 py-3 rounded-lg"
              style={{ 
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                boxShadow: isDark ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="flex justify-between items-center mb-2">
                <div 
                  className="font-medium truncate mr-2" 
                  style={{ 
                    fontSize: `${design.typography.fontSizes['2xl']}px`, 
                    color: textColor, 
                    maxWidth: '60%',
                    fontFamily: design.typography.fontFamily
                  }}
                  title={job.jobName}
                >
                  {job.jobName}
                </div>
                <div 
                    style={{ 
                      fontSize: `${design.typography.fontSizes['2xl'] * design.fontSizeMultiplier}px`, 
                      color: highlightColor,
                      fontFamily: design.typography.fontFamily,
                      fontWeight: design.typography.fontWeights.semibold
                    }}
                  >
                    {formatCurrency(job.potentialSavings)}
                  </div>
              </div>
              
              <div className="flex justify-between items-center mb-1">
                <div 
                  style={{ 
                    fontSize: `${design.typography.fontSizes.base}px`, 
                    color: isDark ? design.colors.textDark.tertiary : design.colors.textLight.tertiary,
                    fontFamily: design.typography.fontFamily
                  }}
                >
                  Current cost: {formatCurrency(job.currentCost)}
                </div>
                <div 
                  style={{ 
                    fontSize: `${design.typography.fontSizes.base}px`, 
                    color: isDark ? design.colors.textDark.tertiary : design.colors.textLight.tertiary,
                    fontFamily: design.typography.fontFamily
                  }}
                >
                  Potential savings: {Math.round((job.potentialSavings / job.currentCost) * 100)}%
                </div>
              </div>
              
              {/* Savings potential bar */}
              <div className="h-1.5 w-full rounded-full overflow-hidden mt-2" style={{ backgroundColor: barBgColor }}>
                <div 
                  className="h-full rounded-full" 
                  style={{ 
                    width: `${Math.round((job.potentialSavings / job.currentCost) * 100)}%`, 
                    backgroundColor: barColor
                  }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavingsOpportunities; 